import React, { useState, useRef } from "react";
import { Upload, X, Eye, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import Tooltip from "./ToolTip";

interface FileUploadProps {
  label: string;
  accept?: string;
  maxSizeMB?: number;
  onFileUpload: (file: File) => Promise<string>;
  currentUrl?: string;
  placeholder?: string;
  className?: string;
  onUrlChange?: (url: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept = "image/*",
  maxSizeMB = 5,
  onFileUpload,
  currentUrl = "",
  placeholder,
  className = "",
  onUrlChange,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>(currentUrl);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update previewUrl when currentUrl prop changes
  React.useEffect(() => {
    setPreviewUrl(currentUrl);
  }, [currentUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = async (file: File) => {
    // Check file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    // Check file type if accept is specified
    if (accept && !file.type.match(accept.replace(/\*/g, ".*"))) {
      toast.error("Invalid file type");
      return;
    }

    setIsUploading(true);

    try {
      const uploadedUrl = await onFileUpload(file);
      setPreviewUrl(uploadedUrl);
      onUrlChange?.(uploadedUrl);
      toast.success("File uploaded successfully!");
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleAreaClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  const clearFile = () => {
    setPreviewUrl("");
    onUrlChange?.("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragOver
            ? 'border-blue-400 bg-blue-50'
            : previewUrl
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${isUploading ? 'pointer-events-none opacity-50' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleAreaClick}
      >
        <div className="space-y-3">
          {isUploading ? (
            <>
              <Loader2 className="mx-auto w-8 h-8 text-blue-500 animate-spin" />
              <div className="text-sm font-medium text-gray-900">
                Uploading...
              </div>
            </>
          ) : (
            <>
              <Upload className={`mx-auto w-8 h-8 ${
                isDragOver ? 'text-blue-500' : previewUrl ? 'text-blue-500' : 'text-gray-400'
              }`} />
              <div className="text-sm font-medium text-gray-900">
                {isDragOver
                  ? 'Drop file here'
                  : previewUrl
                  ? 'File uploaded - click to change'
                  : 'Click to upload or drag and drop'
                }
              </div>
              <div className="text-xs text-gray-500">
                {placeholder || `Max ${maxSizeMB}MB, ${accept}`}
              </div>
            </>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={isUploading}
        />
      </div>

      {/* Current File Info & Actions */}
      {previewUrl && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="text-sm text-blue-800">
              <strong>Current file:</strong> Uploaded successfully
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {accept.includes("image") && (
              <Tooltip content="Preview">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPreview(true);
                }}
                className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Eye className="w-4 h-4" />
              </button>
              </Tooltip>
            )}
            <Tooltip content="Remove">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearFile();
              }}
              className="p-1 text-red-600 hover:text-red-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="text-center">
              <img
                src={previewUrl}
                alt="Preview"
                className="max-w-full max-h-96 mx-auto rounded-lg"
                onError={() => {
                  toast.error("Failed to load image preview");
                  setShowPreview(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;